import { CONTRACT_FIXTURE_IDS, ContractFixtureId } from "src/app/v2/mocks/contracts/contract-fixtures";
import { MockScenario, PRESET_IDS, PresetId } from "src/app/v2/mocks/mock-scenario";

/**
 * Dev-only override written by the AI mock chat widget. Deliberately separate from
 * `dev.mockScenario` (mock-scenario.ts): the widget selects fixtures/booleans directly rather than
 * through a curated preset, and this key must be readable independently so the interceptor can give
 * it priority over the regular mock-scenario dev-row. Mirrors mock-scenario.ts's defensive pattern:
 * pure functions, try/catch around localStorage, no-op/`null` when storage is unavailable.
 */
export const AI_MOCK_OVERRIDE_STORAGE_KEY = "dev.aiMockOverride";

function isPresetId(value: unknown): value is PresetId {
  return typeof value === "string" && (PRESET_IDS as readonly string[]).includes(value);
}

function isFixtureId(value: unknown): value is ContractFixtureId {
  return typeof value === "string" && (CONTRACT_FIXTURE_IDS as readonly string[]).includes(value);
}

/**
 * Unlike `parseMockScenario`, this returns `null` (rather than falling back to a default) on
 * anything malformed: `null` means "no override", so the interceptor falls through to the regular
 * mock scenario instead of silently applying a default one.
 */
function parseAiMockOverride(raw: string): MockScenario | null {
  try {
    const parsed: unknown = JSON.parse(raw);

    if (typeof parsed !== "object" || parsed === null) {
      return null;
    }

    const candidate = parsed as Record<string, unknown>;

    if (
      !isPresetId(candidate["preset"]) ||
      !Array.isArray(candidate["contracts"]) ||
      typeof candidate["partialError"] !== "boolean" ||
      typeof candidate["externalContracts"] !== "boolean" ||
      typeof candidate["navContracts"] !== "boolean" ||
      typeof candidate["hasAllConsents"] !== "boolean"
    ) {
      return null;
    }

    return {
      preset: candidate["preset"],
      contracts: [...new Set(candidate["contracts"].filter(isFixtureId))],
      partialError: candidate["partialError"],
      externalContracts: candidate["externalContracts"],
      navContracts: candidate["navContracts"],
      hasAllConsents: candidate["hasAllConsents"],
    };
  } catch {
    return null;
  }
}

export function readAiMockOverride(): MockScenario | null {
  try {
    const raw = globalThis.localStorage?.getItem(AI_MOCK_OVERRIDE_STORAGE_KEY) ?? null;
    return raw === null ? null : parseAiMockOverride(raw);
  } catch {
    return null;
  }
}

export function writeAiMockOverride(scenario: MockScenario): void {
  try {
    globalThis.localStorage?.setItem(AI_MOCK_OVERRIDE_STORAGE_KEY, JSON.stringify(scenario));
  } catch {
    // Storage unavailable (private mode, blocked cookies) — the override simply does not persist.
  }
}

export function clearAiMockOverride(): void {
  try {
    globalThis.localStorage?.removeItem(AI_MOCK_OVERRIDE_STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
}
